import Highcharts from "highcharts";

// Custom marker symbol for the prior-period tick: a thin fixed-width vertical
// bar drawn inside the point's (radius-based) bounding box. Registered once —
// this makes the tick a real Highcharts point (hoverable, own tooltip),
// instead of a plain SVG shape with no interaction.
if (!(Highcharts.SVGRenderer.prototype.symbols as any).priorTick) {
  (Highcharts.SVGRenderer.prototype.symbols as any).priorTick = (
    x: number,
    y: number,
    w: number,
    h: number,
  ) => {
    const lineWidth = 2;
    const cx = x + w / 2 - lineWidth / 2;
    return ["M", cx, y, "L", cx + lineWidth, y, "L", cx + lineWidth, y + h, "L", cx, y + h, "Z"];
  };
}

// Prior-period comparison, attached to a sub-stage (or the top-level totals).
// "performance" is the semantic driver for color (improving = good = green,
// worsening = bad = red) — NOT "trend", since for a duration metric a
// literal increase ("up") is bad news. "trend" only drives the arrow glyph.
type Comparison = {
  prior_value?: number;
  change?: number;
  change_percent?: number;
  trend?: string;
  display_value?: string;
  normalized_value?: number;
  cases_participated?: number;
  variance_seconds?: number;
  variance_display?: string;
  variance_hours?: number;
  performance?: string;
};

type SubStage = {
  key: string;
  label: string;
  display_value: string;
  normalized_value: number;
  order?: number;
  cases_participated?: number;
  comparison?: Comparison;
};

type Stage = {
  key: string;
  label: string;
  color: string;
  order?: number;
  cases_participated?: number;
  sub_stages: SubStage[];
};

type GroupedBarInput = {
  normalized_unit?: string;
  y_axis_label?: string;
  avg_total_lifecycle_human?: string;
  total_cases_analyzed?: number;
  stages?: Stage[];
  // Overall prior comparison for avg_total_lifecycle_human.
  comparison?: Comparison;
};

// ── Shared comparison presentation helpers ──────────────────────────────────
// Same semantic palette as KpiComparison's trend chip elsewhere in the app
// (green/red/gray), but keyed off "performance" rather than raw trend so the
// color always means "good/bad", never just "number went up".
const PERF_COLORS: Record<string, { text: string; bg: string }> = {
  improving: { text: "#166534", bg: "rgba(22, 101, 52, 0.12)" },
  worsening: { text: "#b91c1c", bg: "rgba(185, 28, 28, 0.12)" },
};
const NEUTRAL_COLOR = { text: "#475569", bg: "rgba(71, 85, 105, 0.12)" };

const perfColor = (cmp?: Comparison): { text: string; bg: string } =>
  PERF_COLORS[String(cmp?.performance ?? "").toLowerCase()] ?? NEUTRAL_COLOR;

const trendArrow = (cmp?: Comparison): string => {
  const trend = String(cmp?.trend ?? "").toLowerCase();
  if (trend === "up") return "▲";
  if (trend === "down") return "▼";
  return "→";
};

const formatPct = (cmp?: Comparison): string => {
  const pct = cmp?.change_percent;
  if (typeof pct !== "number" || !Number.isFinite(pct)) return "";
  const abs = Math.abs(Math.round(pct * 10) / 10);
  const display = Number.isInteger(abs) ? `${abs}` : abs.toFixed(1).replace(/\.0$/, "");
  return `${display}%`;
};

const sortByOrder = <T extends { order?: number }>(items: T[]): T[] =>
  [...items].sort((a, b) => {
    const left = Number.isFinite(Number(a.order))
      ? Number(a.order)
      : Number.MAX_SAFE_INTEGER;
    const right = Number.isFinite(Number(b.order))
      ? Number(b.order)
      : Number.MAX_SAFE_INTEGER;
    return left - right;
  });

const toRgba = (hex: string, alpha: number): string => {
  const normalized = hex.replace("#", "");
  const expanded =
    normalized.length === 3
      ? normalized
          .split("")
          .map((c) => `${c}${c}`)
          .join("")
      : normalized;
  if (expanded.length !== 6) return hex;
  const r = parseInt(expanded.slice(0, 2), 16);
  const g = parseInt(expanded.slice(2, 4), 16);
  const b = parseInt(expanded.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${Math.min(Math.max(alpha, 0), 1)})`;
};

const unwrap = (data: unknown): GroupedBarInput => {
  const raw = (data ?? {}) as Record<string, unknown>;
  if (Array.isArray(raw.stages)) return raw as unknown as GroupedBarInput;
  if (
    raw.data &&
    typeof raw.data === "object" &&
    Array.isArray((raw.data as any).stages)
  )
    return raw.data as GroupedBarInput;
  return raw as unknown as GroupedBarInput;
};

type BarPoint = {
  category: string;
  value: number;
  color: string;
  stageLabel: string;
  subLabel: string;
  displayValue: string;
  isSingle: boolean;
  isSubStage: boolean;
  casesParticipated?: number;
  comparison?: Comparison;
};

type StageGroup = {
  label: string;
  color: string;
  startIndex: number;
  endIndex: number;
};

// ── Left/right margin layout ────────────────────────────────────────────────
// The space left of the bars holds two label lanes that must not overlap: the
// far-left stage (group) lane, which we draw by hand in the render event, and
// the substage category lane Highcharts draws next to the bars. Both wrap to
// their lane width so long names show in full without truncation.
//
// Deliberately NO chart.marginLeft: a fixed margin always reserved the widest
// case, so a chart whose substages are short ("Online" / "Fax") left a big dead
// gap between the stage names and the bars. Leaving marginLeft unset makes
// Highcharts size that lane to the labels it actually measured
// (plotLeft = spacingLeft + rendered label width, see Axis#getOffset), and
// spacingLeft below reserves the stage lane in front of it. So short labels →
// bars slide left; long labels → the lane grows, capped by the wrap width.

const GROUP_LABEL_X = 12;
// Blank space kept between the stage lane and the substage lane.
const LANE_GAP = 12;

// Widest (desktop) lane sizes. LANE_STEPS below shrinks them on narrow widgets.
const BASE_GROUP_LABEL_WIDTH = 96;
const BASE_CATEGORY_LABEL_WIDTH = 140;
// Right-hand gutter the "vs prior" delta chips live in. They're drawn with
// crop:false / overflow:allow, so without this margin they'd be clipped at the
// chart edge. Only reserved when a point actually carries a comparison.
const BASE_MARGIN_RIGHT = 96;
// Gap between a chip and whatever it is anchored to (bar tip or plot edge).
const CHIP_GUTTER_PAD = 8;
// The value label is centred inside its bar, so it is fully contained exactly
// when the bar is as long as the text. This is just breathing room on top of
// that, so a chip at the tip doesn't end up touching the text.
const CHIP_TIP_CLEARANCE = 4;
// Padding Highcharts puts around the value label. Pinned here (rather than left
// at the default) because the placement test has to subtract it: getBBox() on a
// dataLabel returns the padded box, and measuring 10px of invisible padding as
// if it were text wrongly exiled chips whose labels plainly fit — a 50px bar
// holding 39px of text measured as 49px and failed the test.
const VALUE_LABEL_PADDING = 5;

// Series ids, so the render event can find the two series it has to reason
// about without depending on their order in the array.
const BAR_SERIES_ID = "gb-duration";
const CHIP_SERIES_ID = "gb-delta-chips";

// Both anchors a chip can take, carried on the point so the render pass can
// switch between them without recomputing values.
type ChipAnchors = { tipY: number; columnY: number };

const readChipAnchors = (custom: unknown): ChipAnchors | undefined => {
  if (!custom || typeof custom !== "object") return undefined;
  const { tipY, columnY } = custom as Record<string, unknown>;
  return typeof tipY === "number" && typeof columnY === "number"
    ? { tipY, columnY }
    : undefined;
};

// Total room the stage lane needs: its own text width plus the gap to the
// substage labels. Fed to chart.spacingLeft, which Highcharts adds in front of
// the auto-sized axis label lane.
const stageLaneSpacing = (groupLabelWidth: number): number =>
  GROUP_LABEL_X + groupLabelWidth + LANE_GAP;

type LaneStep = {
  maxWidth: number;
  groupLabelWidth: number;
  categoryLabelWidth: number;
  marginRight: number;
  // Set only on the narrowest steps: a long substage name wraps onto a third
  // line there, and at 11px three lines are taller than the row they belong to,
  // so neighbouring labels collide. A notch smaller fits.
  categoryFontSize?: string;
};

// This chart lives in a resizable dashboard grid cell, so how much width the
// text may claim has to fall as the widget narrows. Each step tightens the two
// wrap caps and the chip gutter for widths at or below its maxWidth.
//
// The two label widths are wrap CAPS, not reserved space — the lanes are
// measured and fitted at render time, so a generous cap costs nothing when the
// names are short. Keep the category cap roomy: squeezing it pushes long
// substage names onto a third line, which collides with the neighbouring row
// long before the extra plot width is worth it.
//
// Ordered widest → narrowest on purpose: Highcharts merges EVERY rule whose
// condition matches (it tests `chartWidth <= maxWidth`) in array order, so at
// 340px every step matches and the last — narrowest — one wins. Keep that
// ordering when editing this table.
const LANE_STEPS: LaneStep[] = [
  { maxWidth: 720, groupLabelWidth: 88, categoryLabelWidth: 132, marginRight: 92 },
  { maxWidth: 640, groupLabelWidth: 80, categoryLabelWidth: 126, marginRight: 88 },
  { maxWidth: 560, groupLabelWidth: 72, categoryLabelWidth: 120, marginRight: 84 },
  { maxWidth: 480, groupLabelWidth: 64, categoryLabelWidth: 112, marginRight: 80 },
  {
    maxWidth: 420,
    groupLabelWidth: 56,
    categoryLabelWidth: 104,
    marginRight: 76,
    categoryFontSize: "10px",
  },
  {
    maxWidth: 360,
    groupLabelWidth: 48,
    categoryLabelWidth: 92,
    marginRight: 72,
    categoryFontSize: "10px",
  },
];

// The stage-lane width in effect at a given chart width — same predicate and
// same last-match-wins order Highcharts applies to the rules built from
// LANE_STEPS, so the hand-drawn lane always matches the spacing reserved for it.
const groupLaneWidth = (chartWidth: number): number =>
  LANE_STEPS.filter((s) => chartWidth <= s.maxWidth).pop()?.groupLabelWidth ??
  BASE_GROUP_LABEL_WIDTH;

const buildGroupedBarOptions = (data: unknown): Highcharts.Options => {
  const input = unwrap(data);
  const stages = Array.isArray(input.stages) ? sortByOrder(input.stages) : [];
  const unit = input.normalized_unit ?? "hours";
  const lifecycleHuman = input.avg_total_lifecycle_human ?? "";

  const points: BarPoint[] = [];
  const stageGroups: StageGroup[] = [];

  stages.forEach((stage) => {
    const subStages = Array.isArray(stage.sub_stages)
      ? sortByOrder(stage.sub_stages)
      : [];
    if (subStages.length === 0) return;

    const isSingle = subStages.length === 1;
    const startIndex = points.length;

    subStages.forEach((sub, subIdx) => {
      const shade = !isSingle
        ? toRgba(stage.color, Math.max(0.92 - subIdx * 0.18, 0.45))
        : stage.color;

      points.push({
        category: isSingle ? stage.label : sub.label,
        value: sub.normalized_value,
        color: shade,
        stageLabel: stage.label,
        subLabel: sub.label,
        displayValue: sub.display_value,
        isSingle,
        isSubStage: !isSingle,
        casesParticipated: sub.cases_participated ?? stage.cases_participated,
        comparison: sub.comparison,
      });
    });

    stageGroups.push({
      label: stage.label,
      color: stage.color,
      startIndex,
      endIndex: points.length - 1,
    });
  });

  const categories = points.map((p) => p.category);
  const chartHeight = Math.max(320, points.length * 58 + 90);
  const hasAnyComparison = points.some((p) => p.comparison);

  // The prior-period tick can sit past the current bar's own length (a
  // substage can have gotten faster or slower since). Without this, the
  // y-axis auto-scales to the current values only and a prior tick beyond
  // that range would render off the plot.
  const axisMax = hasAnyComparison
    ? Math.max(
        ...points.map((p) => p.value),
        ...points.map((p) => p.comparison?.normalized_value ?? 0),
      ) * 1.08
    : undefined;

  // Prior-period tick points — a real scatter series (not a plain drawn
  // shape) so each tick is genuinely hoverable with its own tooltip. Sparse:
  // only points whose sub-stage has a comparison get an entry, with an
  // explicit x (category index) since the array is no longer 1:1 with rows.
  const priorPoints = points.reduce<Array<Record<string, unknown>>>(
    (acc, p, i) => {
      const priorValue = p.comparison?.normalized_value;
      if (typeof priorValue === "number" && Number.isFinite(priorValue)) {
        acc.push({
          x: i,
          y: priorValue,
          custom: {
            stageLabel: p.stageLabel,
            subLabel: p.subLabel,
            isSingle: p.isSingle,
            comparison: p.comparison,
          },
        });
      }
      return acc;
    },
    [],
  );

  // One chip per point that has a comparison. Each carries both anchors it can
  // take — its own bar's tip, or the axis max (the aligned gutter column) — and
  // starts in the column, which is the placement that can never overlap. The
  // render pass promotes a chip to its bar's tip once it can measure that the
  // bar has room to spare.
  const chipPoints = points.reduce<Array<Record<string, unknown>>>(
    (acc, p, i) => {
      if (p.comparison) {
        const prior = p.comparison.normalized_value;
        // Anchor past whichever is further right, the bar tip or the prior-period
        // tick, so a tip-anchored chip clears the tick as well as the bar.
        const tipY =
          typeof prior === "number" && Number.isFinite(prior)
            ? Math.max(p.value, prior)
            : p.value;
        const columnY = axisMax ?? p.value;
        acc.push({
          x: i,
          y: columnY,
          custom: { comparison: p.comparison, tipY, columnY },
        });
      }
      return acc;
    },
    [],
  );

  const series: Highcharts.SeriesOptionsType[] = [
    {
      type: "bar" as const,
      id: BAR_SERIES_ID,
      name: "Duration",
      showInLegend: false,
      colorByPoint: true,
      data: points.map((p) => ({
        y: p.value,
        color: p.color,
        custom: {
          displayValue: p.displayValue,
          subLabel: p.subLabel,
          stageLabel: p.stageLabel,
          isSingle: p.isSingle,
          isSubStage: p.isSubStage,
          casesParticipated: p.casesParticipated,
          // Not read by the bar's own tooltip (hovering a bar shows only its
          // current data) — kept so the point stays self-describing for the
          // export menu and for anything reading series data later.
          comparison: p.comparison,
        },
      })),
    },
    // Rendered after the bar series so its marker sits on top and wins
    // hover priority over the bar for the small area the tick occupies.
    {
      type: "scatter" as const,
      name: "Prior period avg",
      showInLegend: false,
      data: priorPoints,
      marker: {
        symbol: "priorTick",
        radius: 17,
        fillColor: "#0F172A",
        lineWidth: 0,
        states: { hover: { fillColorOpacity: 1 } },
      },
    } as any,
    // Delta chips ride their own invisible series rather than being a second
    // dataLabel on the bar. As a bar dataLabel they were pinned 6px past the
    // bar's tip, which on a short bar is exactly where that bar's own value
    // label already is — the two printed on top of each other. As a series they
    // can be anchored per point (bar tip when there's room, axis max otherwise),
    // which is what the placement pass in render() decides.
    ...(hasAnyComparison
      ? [
          {
            type: "scatter" as const,
            id: CHIP_SERIES_ID,
            name: "Change vs prior",
            showInLegend: false,
            // Purely a label carrier: no marker, and it must never steal a
            // hover from the bar or the prior tick underneath it.
            enableMouseTracking: false,
            marker: { enabled: false },
            data: chipPoints,
            dataLabels: {
              enabled: true,
              useHTML: true,
              allowOverlap: true,
              crop: false,
              overflow: "allow" as const,
              align: "left" as const,
              verticalAlign: "middle" as const,
              x: CHIP_GUTTER_PAD,
              style: { fontSize: "10px" },
              formatter() {
                const cmp = (this as unknown as { point: { options: { custom?: { comparison?: Comparison } } } })
                  .point.options.custom?.comparison;
                if (!cmp) return null as unknown as string;
                const { text, bg } = perfColor(cmp);
                return `<span style="display:inline-flex;align-items:center;gap:3px;padding:2px 7px;border-radius:999px;font-size:10px;font-weight:700;color:${text};background:${bg};white-space:nowrap;">${trendArrow(cmp)} ${formatPct(cmp)}</span>`;
              },
            },
          },
        ]
      : []),
  ];

  // Shrink both label lanes — and the delta-chip gutter, when there are chips
  // to make room for — as the widget narrows. See LANE_STEPS for the ordering
  // rule these depend on.
  const responsiveRules: Highcharts.ResponsiveRulesOptions[] = LANE_STEPS.map(
    (step) => ({
      condition: { maxWidth: step.maxWidth },
      chartOptions: {
        // spacingLeft is deliberately NOT set here — the render event owns it
        // (it fits the lane to the measured stage names). A rule setting it too
        // would fight that fit every time the rules re-apply.
        ...(hasAnyComparison
          ? { chart: { marginRight: step.marginRight } }
          : {}),
        xAxis: {
          // Deep-merged into the base label style below, so only the keys named
          // here change — color/weight carry over.
          labels: {
            style: {
              width: step.categoryLabelWidth,
              ...(step.categoryFontSize
                ? { fontSize: step.categoryFontSize }
                : {}),
            } as Highcharts.CSSObject,
          },
        },
      },
    }),
  );

  return {
    chart: {
      type: "bar",
      animation: false,
      height: chartHeight,
      marginTop: lifecycleHuman ? 40 : 10,
      // No marginLeft on purpose — see the layout notes above. spacingLeft
      // reserves the stage lane; Highcharts adds the substage lane it measured.
      spacingLeft: stageLaneSpacing(BASE_GROUP_LABEL_WIDTH),
      marginRight: hasAnyComparison ? BASE_MARGIN_RIGHT : undefined,
      // Nothing renders below the plot: no axis labels, no title, and no
      // legend (the stage colors are already named in the left-hand lane, so
      // the legend was duplication — and it got clipped on narrow widgets).
      // The small margin is only slack for the bottom row's wrapped label.
      marginBottom: 16,
      events: {
        render() {
          const chart = this as Highcharts.Chart & {
            _gbGroups?: Highcharts.SVGElement[];
            _gbSubtitle?: Highcharts.SVGElement;
            _gbDividers?: Highcharts.SVGElement[];
          };

          if (chart._gbGroups) {
            chart._gbGroups.forEach((el) => el.destroy());
          }
          chart._gbGroups = [];

          if (chart._gbSubtitle) {
            chart._gbSubtitle.destroy();
            chart._gbSubtitle = undefined;
          }
          if (lifecycleHuman) {
            const overallCmp = input.comparison;
            const caption = `<span style="color:#64748B;font-size:10px;">Total Avg Time </span>`;
            const value = `<span style="font-weight:700;font-size:12px;color:#0F172A;">${lifecycleHuman}</span>`;
            const chip = (suffix: string): string =>
              overallCmp
                ? ` <span style="font-weight:700;font-size:11px;color:${perfColor(overallCmp).text};">${trendArrow(overallCmp)} ${formatPct(overallCmp)}${suffix}</span>`
                : "";

            // The badge is pinned to the chart's right edge, so on a narrow
            // widget the full wording overflowed the chart box (clipped) or got
            // pushed back over the bars. Drop wording, widest variant first,
            // until one fits — measured with getBBox since the text width
            // depends on the rendered font.
            const variants = [
              caption + value + chip(" vs Prior"),
              caption + value + chip(""),
              value + chip(""),
              value,
            ];
            const maxBadgeWidth = chart.chartWidth - 24;

            for (let v = 0; v < variants.length; v++) {
              const badge = chart.renderer
                .label(variants[v]!, 0, 6)
                .attr({
                  zIndex: 7,
                  padding: 6,
                  r: 6,
                  fill: "#F1F5F9",
                  stroke: "#E2E8F0",
                  "stroke-width": 1,
                })
                .css({ fontSize: "11px" })
                .add();

              const bbox = badge.getBBox();
              // The bare value is kept whether it fits or not — a slightly
              // cramped badge beats no total at all.
              if (bbox.width <= maxBadgeWidth || v === variants.length - 1) {
                badge.attr({
                  x: Math.max(6, chart.chartWidth - bbox.width - 12),
                });
                chart._gbSubtitle = badge;
                break;
              }
              badge.destroy();
            }
          }

          const xAxis = chart.xAxis[0];
          if (!xAxis) return;

          // Follows the chart's current width, so the hand-drawn stage lane
          // shrinks in step with the axis labels the responsive rules resized.
          const groupLabelWidth = groupLaneWidth(chart.chartWidth);
          // Widest stage name once wrapped — measured below and used to fit the
          // lane afterwards.
          let widestGroupLabel = 0;

          stageGroups.forEach((group) => {
            const topPos = xAxis.toPixels(group.startIndex, true);
            const bottomPos = xAxis.toPixels(group.endIndex, true);
            const midY = chart.plotTop + (topPos + bottomPos) / 2;
            const stripeX = 6;
            const barTop = chart.plotTop + Math.min(topPos, bottomPos) - 8;
            const barHeight = Math.abs(bottomPos - topPos) + 16;

            const stripe = chart.renderer
              .rect(stripeX, barTop, 3, barHeight, 1)
              .attr({ fill: group.color, zIndex: 7 })
              .add();
            chart._gbGroups!.push(stripe);

            // Stage name wraps to its lane width (SVG tspans) so long names
            // show in full — no truncation. Anchor a little above the group
            // midpoint so a 2-line name stays visually centred on its bars.
            const groupLabel = chart.renderer
              .text(group.label, GROUP_LABEL_X, midY)
              .attr({ zIndex: 7 })
              .css({
                fontSize: "10px",
                fontWeight: "700",
                color: group.color,
                width: groupLabelWidth,
                whiteSpace: "normal",
              } as Highcharts.CSSObject)
              .add();

            // Vertically re-centre now that the wrapped height is known.
            const groupBox = groupLabel.getBBox();
            groupLabel.attr({ y: midY - groupBox.height / 2 + 10 });
            widestGroupLabel = Math.max(widestGroupLabel, groupBox.width);
            chart._gbGroups!.push(groupLabel);
          });

          // Divider between stage (main) groups — a thin full-width rule so
          // e.g. "Medical Benefit"'s rows read as visually distinct from
          // "Pharmacy Benefit"'s, not just via the left-margin stripe/label.
          if (chart._gbDividers) {
            chart._gbDividers.forEach((el) => el.destroy());
          }
          chart._gbDividers = [];

          for (let g = 0; g < stageGroups.length - 1; g++) {
            const cur = stageGroups[g]!;
            const next = stageGroups[g + 1]!;
            const curEndY = chart.plotTop + xAxis.toPixels(cur.endIndex, true);
            const nextStartY =
              chart.plotTop + xAxis.toPixels(next.startIndex, true);
            const boundaryY = (curEndY + nextStartY) / 2;

            const divider = chart.renderer
              .path(["M", 0, boundaryY, "L", chart.chartWidth, boundaryY])
              .attr({ stroke: "#E2E8F0", "stroke-width": 1, zIndex: 6 })
              .add();
            chart._gbDividers!.push(divider);
          }

          // Fit the stage lane to the names actually rendered. groupLabelWidth
          // is only a wrap cap: "Fax" needs ~25px where "Pending Approval to
          // Submitted" needs the whole lane, and spacingLeft has to be decided
          // before a single label is measured. So we measure what we just drew
          // and hand any slack back to the bars — this is what stops a short
          // stage name from leaving a dead gap in front of the plot.
          //
          // Safe against a redraw loop: the update only fires when the fitted
          // value differs from the applied one, and re-measuring after it lands
          // yields the same number (the wrap cap didn't move), so the next
          // render is a no-op.
          const fittedSpacing = stageLaneSpacing(Math.ceil(widestGroupLabel));
          const appliedSpacing =
            chart.options.chart?.spacingLeft ??
            stageLaneSpacing(BASE_GROUP_LABEL_WIDTH);
          if (widestGroupLabel > 0 && Math.abs(appliedSpacing - fittedSpacing) > 2) {
            chart.update({ chart: { spacingLeft: fittedSpacing } }, true, false, false);
            // The update redraws, so this handler runs again with the settled
            // geometry. Bail out now rather than place the chips against a plot
            // width that is about to change.
            return;
          }

          // ── Delta chip placement, decided per bar ─────────────────────────
          // A chip just past its own bar's tip reads better than one parked in
          // the gutter — the change belongs to that bar. But that only works
          // while the bar is long enough to hold the value label printed inside
          // it: once it isn't, the label spills past the tip into exactly the
          // space the chip wants. So each chip is judged on its own bar, and
          // only the ones whose bars are too short fall back to the aligned
          // column at the axis max. Wide widget → nearly all at the tips;
          // shrink it and they drop into the column as their bars run out of
          // room.
          const chipSeries = chart.series.find(
            (s) => s.options.id === CHIP_SERIES_ID,
          );
          const barSeries = chart.series.find(
            (s) => s.options.id === BAR_SERIES_ID,
          );
          const yAxis = chart.yAxis[0];

          if (chipSeries && barSeries && yAxis) {
            const zeroPx = yAxis.toPixels(0, true);
            let anyMoved = false;

            const nextChipData = chipSeries.points.map((chip) => {
              const anchors = readChipAnchors(chip.options.custom);
              // The bar sharing this chip's category index. Bar data is 1:1
              // with the category list, so the index maps straight across.
              const bar = barSeries.points[chip.x] as
                | (Highcharts.Point & { dataLabel?: Highcharts.SVGElement })
                | undefined;

              let y = chip.y ?? 0;
              if (anchors && bar) {
                const barLength = Math.abs(
                  yAxis.toPixels(bar.y ?? 0, true) - zeroPx,
                );
                // Measured, not estimated: the value label is already drawn by
                // the time the chart's render event fires. Padding comes back
                // out of the box width — only the text can actually collide.
                const labelBox = bar.dataLabel?.getBBox();
                const labelTextWidth = labelBox
                  ? Math.max(labelBox.width - VALUE_LABEL_PADDING * 2, 0)
                  : 0;
                y =
                  barLength >= labelTextWidth + CHIP_TIP_CLEARANCE
                    ? anchors.tipY
                    : anchors.columnY;
              }
              if (y !== chip.y) anyMoved = true;
              return { x: chip.x, y, custom: chip.options.custom };
            });

            // Only ever fires when a chip actually changes anchor. Re-running
            // the pass after the redraw yields the same verdict (bar lengths
            // and label widths didn't move, and the chips live outside the plot
            // so they can't rescale the axis), so the next render is a no-op.
            if (anyMoved) {
              chipSeries.setData(nextChipData, true);
            }
          }
        },
      },
    },
    title: { text: "" },
    credits: { enabled: false },
    accessibility: { enabled: false },
    xAxis: {
      categories,
      title: { text: null },
      lineWidth: 0,
      tickWidth: 0,
      labels: {
        // SVG text wrapping (NOT useHTML). HTML axis labels on inverted (bar)
        // charts ignore `width` and collapse to one character per line; native
        // SVG labels wrap at word boundaries so long stage names show in full,
        // no truncation. Width bounds the wrap; whiteSpace:normal enables it.
        useHTML: false,
        style: {
          color: "#334155",
          fontSize: "11px",
          fontWeight: "500",
          // Narrowed by the responsive rules on small widgets; the names keep
          // wrapping rather than truncating, so nothing is ever hidden.
          width: BASE_CATEGORY_LABEL_WIDTH,
          whiteSpace: "normal",
        } as Highcharts.CSSObject,
      },
    },
    // No axis chrome: every bar already carries its own value (+ delta chip)
    // as a label, so the tick numbers/unit title/vertical gridlines were
    // pure duplication — and on a shared 0–260-ish hour scale, they read as
    // clutter more than guidance.
    yAxis: {
      min: 0,
      max: axisMax,
      title: { text: null },
      labels: { enabled: false },
      gridLineWidth: 0,
      lineWidth: 0,
      tickWidth: 0,
    },
    tooltip: {
      shared: false,
      useHTML: true,
      outside: true,
      borderWidth: 0,
      borderRadius: 10,
      backgroundColor: "#0F172A",
      shadow: false,
      padding: 0,
      formatter() {
        const ctx = this as any;
        const c = ctx?.point?.options?.custom ?? {};
        const stageLabel = c.stageLabel ?? "";
        const subLabel = c.subLabel ?? "";
        const isSingle = Boolean(c.isSingle);

        // Hovering the prior-period tick: only that period's own data —
        // no current value, no delta.
        if (ctx?.series?.type === "scatter") {
          const cmp = c.comparison as Comparison | undefined;
          if (!cmp) return false;

          const casesRow =
            cmp.cases_participated !== undefined
              ? `<div style="margin-top:6px;padding-top:6px;border-top:1px solid rgba(255,255,255,0.15);display:flex;align-items:center;gap:6px;">
                  <span style="opacity:0.7;font-size:11px;">Cases:</span>
                  <span style="font-weight:700;font-size:12px;color:#7DD3FC;">${cmp.cases_participated.toLocaleString()}</span>
                </div>`
              : "";

          return `
            <div style="padding:10px 12px;min-width:200px;color:#f8fafc;font-size:12px;line-height:1.35;">
              <div style="font-weight:700;font-size:13px;margin-bottom:2px;">${stageLabel}</div>
              ${!isSingle ? `<div style="opacity:0.78;font-size:11px;margin-bottom:6px;">${subLabel}</div>` : ""}
              <div style="opacity:0.65;font-size:11px;margin-bottom:2px;">Prior period</div>
              <div style="font-weight:700;font-size:15px;margin-bottom:2px;">${cmp.display_value ?? "–"}</div>
              <div style="opacity:0.6;font-size:11px;">${cmp.normalized_value ?? ""} ${unit}</div>
              ${casesRow}
            </div>
          `;
        }

        // Hovering the bar: only the current period's own data.
        const displayValue = c.displayValue ?? "";
        const casesParticipated = c.casesParticipated as number | undefined;

        const casesRow =
          casesParticipated !== undefined
            ? `<div style="margin-top:6px;padding-top:6px;border-top:1px solid rgba(255,255,255,0.15);display:flex;align-items:center;gap:6px;">
                <span style="opacity:0.7;font-size:11px;">Cases:</span>
                <span style="font-weight:700;font-size:12px;color:#7DD3FC;">${casesParticipated.toLocaleString()}</span>
              </div>`
            : "";

        return `
          <div style="padding:10px 12px;min-width:200px;color:#f8fafc;font-size:12px;line-height:1.35;">
            <div style="font-weight:700;font-size:13px;margin-bottom:2px;">${stageLabel}</div>
            ${!isSingle ? `<div style="opacity:0.78;font-size:11px;margin-bottom:6px;">${subLabel}</div>` : ""}
            <div style="font-weight:700;font-size:15px;margin-bottom:2px;">${displayValue}</div>
            <div style="opacity:0.6;font-size:11px;">${ctx.y} ${unit}</div>
            ${casesRow}
          </div>
        `;
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 3,
        // maxPointWidth, not pointWidth: the widget's height is set by its grid
        // cell, so with a fixed 30px thickness a short widget with many rows
        // drew bars taller than their own row slot and they overlapped. Now the
        // bar follows the slot height and just stops growing past 30px, which
        // is what it already resolved to at the usual widget height.
        maxPointWidth: 30,
        pointPadding: 0.05,
        groupPadding: 0.12,
        borderWidth: 0,
        states: {
          inactive: { opacity: 0.35 },
          hover: { enabled: true, brightness: 0.06 },
        },
        // Just the value, inside the bar. The delta chip is no longer a second
        // label here — it's the "Change vs prior" series above, so it can't
        // land on top of this one when the bar is too short to hold it.
        dataLabels: {
          enabled: true,
          inside: true,
          // Pinned so the chip placement pass can subtract it again — see
          // VALUE_LABEL_PADDING. Invisible either way (the label has no fill or
          // border), and it doesn't shift the text, which stays centred.
          padding: VALUE_LABEL_PADDING,
          style: {
            color: "#0B1220",
            fontWeight: "700",
            textOutline: "none",
            fontSize: "10px",
          },
          formatter() {
            const c = (this as any).point?.options?.custom ?? {};
            return c.displayValue ?? "";
          },
        },
      },
    },
    legend: { enabled: false },
    responsive: { rules: responsiveRules },
    exporting: {
      enabled: true,
      fallbackToExportServer: true,
      buttons: {
        contextButton: {
          align: "right",
          verticalAlign: "top",
          y: -43,
          x: -20,
        },
      },
    },
    series,
  };
};

export function renderGroupedBar(
  data: unknown,
  _spec: unknown,
): Highcharts.Options {
  return buildGroupedBarOptions(data);
}
